import { useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  Edit3,
  Eye as EyeIcon,
  Mail,
  RotateCcw,
  Search,
  Send,
  User,
} from 'lucide-react'
import DOMPurify from 'dompurify'
import { toast } from 'react-toastify'

import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import { useEditorCore, EditorToolbar } from 'components/Editor/EditorCore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import 'components/Editor/Editor.css'
import {
  useGetCampaignLeadReview,
  type CampaignLeadReviewItem,
} from '../campaigns_api'
import { useGetCampaignSequence, type SequenceStep } from '../sequences_api'
import {
  useGetEmailOverrides,
  useSaveEmailOverrides,
  useDeleteEmailOverride,
  useApplyEmailOverrides,
} from '../email_overrides_api'
import {
  detectUnfilledVariables,
  fillVariables,
  getMissingValuesLabel,
  getMissingValuesMessage,
  mergeVariables,
  type EmailVariable,
} from '../email-variables'
import { useCampaignVariables } from '../columns/useCampaignVariables'
import { useCampaignAITokensByContact } from '../columns/useCampaignAITokens'
import { useGetCampaignSenderEmails } from '../sender_emails_api'
import { useGetCampaignSettings } from '../campaign_settings_api'
import { EmailFooterPreview } from '../EmailFooterPreview'
import { FormatBadge } from '../FormatBadge'
import { htmlToBisonPlainText } from '../htmlToBisonPlainText'

import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'
import { SendStepTestEmailModal } from '../SendStepTestEmailModal'

interface WizardReviewEmailsStepProps {
  campaignId: number
  bisonCampaignId: number
  onBack: () => void
  onNext: () => void
  stepNumber: number
  totalSteps: number
  isLiveCampaign?: boolean
  readOnly?: boolean
}

type ContactTokens = Record<string, string>

function buildTokens(contact: CampaignLeadReviewItem): ContactTokens {
  const [firstName = '', ...rest] = (contact.full_name || '')
    .trim()
    .split(' ')
    .filter(Boolean)
  const lastName = rest.join(' ')
  // Skip empty values so fillVariables leaves the placeholder literal — that
  // way detectUnfilledVariables surfaces it in the warning panel rather than
  // silently producing "Hi ," for contacts with no first name.
  const tokens: ContactTokens = {}
  if (firstName) tokens.FIRST_NAME = firstName
  if (lastName) tokens.LAST_NAME = lastName
  if (contact.email) tokens.EMAIL = contact.email
  if (contact.company_name) tokens.COMPANY = contact.company_name
  if (contact.title) tokens.TITLE = contact.title
  return tokens
}

/** Key format: `${leadId}_${stepId}` */
type OverrideKey = string

interface LocalOverride {
  subject: string
  body: string
}

export function WizardReviewEmailsStep({
  campaignId,
  bisonCampaignId,
  onBack,
  onNext,
  stepNumber,
  totalSteps,
  isLiveCampaign = false,
  readOnly = false,
}: WizardReviewEmailsStepProps) {
  const { data: leadReview, isLoading: leadsLoading } =
    useGetCampaignLeadReview(campaignId)
  const { data: sequence, isLoading: seqLoading } =
    useGetCampaignSequence(campaignId)
  const { data: savedOverrides = [], isLoading: overridesLoading } =
    useGetEmailOverrides(bisonCampaignId)
  const saveMutation = useSaveEmailOverrides(bisonCampaignId)
  const deleteMutation = useDeleteEmailOverride(bisonCampaignId)
  const applyMutation = useApplyEmailOverrides(bisonCampaignId)
  const { data: senderEmails } = useGetCampaignSenderEmails(bisonCampaignId)
  const senderSignature = senderEmails?.results?.[0]?.email_signature ?? ''
  const { data: campaignSettings } = useGetCampaignSettings(
    campaignId,
    bisonCampaignId > 0
  )
  const isPlainText = campaignSettings?.plain_text ?? true

  const customVariables = useCampaignVariables(campaignId)
  const allVariables = mergeVariables(customVariables)
  const aiTokensByContact = useCampaignAITokensByContact(campaignId)

  const contacts = useMemo(
    () =>
      (leadReview?.results ?? []).filter(
        (c) => c.is_included && c.email && c.bison_lead_id != null
      ),
    [leadReview]
  )
  const steps = sequence?.steps ?? []

  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  )
  const [activeStepId, setActiveStepId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [editingKey, setEditingKey] = useState<OverrideKey | null>(null)
  const [localOverrides, setLocalOverrides] = useState<
    Record<OverrideKey, LocalOverride>
  >({})
  const [savingKeys, setSavingKeys] = useState<Set<OverrideKey>>(new Set())
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)

  // Initialize selections once data loads
  const resolvedContactId =
    selectedContactId ?? (contacts.length > 0 ? contacts[0].contact_id : null)
  const resolvedStepId = activeStepId ?? (steps.length > 0 ? steps[0].id : null)

  const selectedContact = contacts.find(
    (c) => c.contact_id === resolvedContactId
  )
  const currentStep = steps.find((s) => s.id === resolvedStepId)

  // Build merged override lookup: server-saved + local edits
  const overrideMap = useMemo(() => {
    const map: Record<OverrideKey, LocalOverride> = {}
    for (const o of savedOverrides) {
      map[`${o.lead_id}_${o.step_id}`] = {
        subject: o.custom_subject,
        body: o.custom_body,
      }
    }
    // Local overrides take precedence
    for (const [key, val] of Object.entries(localOverrides)) {
      map[key] = val
    }
    return map
  }, [savedOverrides, localOverrides])

  // Contacts that have overrides (for badges)
  const contactsWithOverrides = useMemo(() => {
    const ids = new Set<number>()
    for (const key of Object.keys(overrideMap)) {
      const leadId = parseInt(key.split('_')[0], 10)
      if (!isNaN(leadId)) ids.add(leadId)
    }
    return ids
  }, [overrideMap])

  // Map contact_id → bison_lead_id for override key generation
  const leadIdForContact = useMemo(() => {
    const map: Record<number, number> = {}
    for (const c of contacts) {
      if (c.bison_lead_id) {
        map[c.contact_id] = c.bison_lead_id
      }
    }
    return map
  }, [contacts])

  const resolvedLeadId = resolvedContactId
    ? leadIdForContact[resolvedContactId]
    : undefined
  const overrideKey =
    resolvedLeadId && resolvedStepId
      ? `${resolvedLeadId}_${resolvedStepId}`
      : null
  const hasOverride = overrideKey
    ? overrideMap[overrideKey] !== undefined
    : false
  const isEditing = editingKey !== null && editingKey === overrideKey

  // Count only persisted overrides, not unsaved local edits
  const totalOverrides = savedOverrides.length
  const primaryButtonBaseLabel = isLiveCampaign ? 'Done' : 'Continue to Launch'
  const primaryButtonLabel =
    totalOverrides > 0
      ? `${primaryButtonBaseLabel} (${totalOverrides} custom edit${totalOverrides !== 1 ? 's' : ''})`
      : primaryButtonBaseLabel

  // Search filter
  const filteredContacts = useMemo(() => {
    if (!searchQuery) return contacts
    const q = searchQuery.toLowerCase()
    return contacts.filter(
      (c) =>
        (c.full_name || '').toLowerCase().includes(q) ||
        (c.company_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
    )
  }, [contacts, searchQuery])

  // Get rendered email for a contact + step
  function getRenderedEmail(
    step: SequenceStep,
    contact: CampaignLeadReviewItem
  ) {
    const leadId = leadIdForContact[contact.contact_id]
    const key = leadId ? `${leadId}_${step.id}` : null
    const override = key ? overrideMap[key] : undefined
    const tokens = {
      ...buildTokens(contact),
      ...(aiTokensByContact[contact.contact_id] ?? {}),
    }

    if (override) {
      return {
        subject: fillVariables(override.subject, tokens),
        body: fillVariables(override.body, tokens, { escapeValues: true }),
      }
    }
    return {
      subject: fillVariables(step.email_subject, tokens),
      body: fillVariables(step.email_body, tokens, { escapeValues: true }),
    }
  }

  function getRenderedEmailWarnings(
    step: SequenceStep,
    contact: CampaignLeadReviewItem
  ): string[] {
    const rendered = getRenderedEmail(step, contact)
    return Array.from(
      new Set([
        ...detectUnfilledVariables(rendered.subject),
        ...detectUnfilledVariables(rendered.body),
      ])
    )
  }

  const unresolvedEmailWarnings = useMemo(() => {
    return contacts.flatMap((contact) =>
      steps
        .map((step) => ({
          contact,
          step,
          warnings: getRenderedEmailWarnings(step, contact),
        }))
        .filter((item) => item.warnings.length > 0)
    )
    // The rendered values depend on these maps in addition to contacts/steps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, steps, leadIdForContact, overrideMap, aiTokensByContact])

  // Render the selected contact's email once per state change and reuse it
  // for the preview panes + the per-contact test send payload. The deps below
  // cover everything getRenderedEmail closes over (template, override, tokens).
  const selectedRendered = useMemo(
    () =>
      currentStep && selectedContact
        ? getRenderedEmail(currentStep, selectedContact)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentStep,
      selectedContact,
      overrideMap,
      aiTokensByContact,
      leadIdForContact,
    ]
  )

  const selectedEmailWarnings = useMemo(
    () =>
      selectedRendered
        ? Array.from(
            new Set([
              ...detectUnfilledVariables(selectedRendered.subject),
              ...detectUnfilledVariables(selectedRendered.body),
            ])
          )
        : [],
    [selectedRendered]
  )
  const hasSelectedEmailWarnings = selectedEmailWarnings.length > 0
  const canSaveCurrentOverride =
    !hasSelectedEmailWarnings && !savingKeys.has(overrideKey!)

  const renderedForSelectedContact =
    selectedRendered && selectedContact
      ? {
          subject: selectedRendered.subject,
          body: isPlainText
            ? htmlToBisonPlainText(selectedRendered.body)
            : selectedRendered.body,
          plainText: isPlainText,
          contactName: selectedContact.full_name || '',
        }
      : null

  function startEditing() {
    if (!currentStep || !selectedContact || !overrideKey) return
    if (!overrideMap[overrideKey]) {
      // Pre-fill with the template (with variable tokens preserved for editing)
      setLocalOverrides((prev) => ({
        ...prev,
        [overrideKey]: {
          subject: currentStep.email_subject,
          body: currentStep.email_body,
        },
      }))
    }
    setEditingKey(overrideKey)
  }

  async function saveEdit() {
    if (!overrideKey || !resolvedStepId) return

    const override = overrideMap[overrideKey]
    if (!override) {
      setEditingKey(null)
      return
    }

    if (!resolvedLeadId) return
    const leadId = resolvedLeadId

    setSavingKeys((prev) => new Set(prev).add(overrideKey))
    try {
      await saveMutation.mutateAsync({
        overrides: [
          {
            lead_id: leadId,
            step_id: resolvedStepId,
            custom_subject: override.subject,
            custom_body: override.body,
          },
        ],
      })
      // Clear local override since it's now persisted
      setLocalOverrides((prev) => {
        const next = { ...prev }
        delete next[overrideKey]
        return next
      })
      setEditingKey(null)
      // For live campaigns, apply overrides to Bison immediately
      if (isLiveCampaign) {
        try {
          await applyMutation.mutateAsync()
          toast.success('Override applied to live campaign')
        } catch {
          toast.error('Override saved but failed to apply to live campaign')
        }
      }
    } catch {
      toast.error('Failed to save override')
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev)
        next.delete(overrideKey)
        return next
      })
    }
  }

  async function resetOverride() {
    if (!overrideKey) return

    // Find persisted override to delete
    const persisted = savedOverrides.find(
      (o) => `${o.lead_id}_${o.step_id}` === overrideKey
    )

    // Clear local
    setLocalOverrides((prev) => {
      const next = { ...prev }
      delete next[overrideKey]
      return next
    })
    setEditingKey(null)

    // Delete from server if persisted
    if (persisted) {
      try {
        await deleteMutation.mutateAsync(persisted.id)
        if (isLiveCampaign) {
          await applyMutation.mutateAsync()
        }
      } catch {
        toast.error('Failed to reset override')
      }
    }
  }

  function updateOverride(field: 'subject' | 'body', value: string) {
    if (!overrideKey) return
    setLocalOverrides((prev) => ({
      ...prev,
      [overrideKey]: {
        ...(prev[overrideKey] ??
          overrideMap[overrideKey] ?? { subject: '', body: '' }),
        [field]: value,
      },
    }))
  }

  const isLoading = leadsLoading || seqLoading || overridesLoading

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          <p className="text-sm text-fg-secondary">Loading email review...</p>
        </div>
      </div>
    )
  }

  if (contacts.length === 0 || steps.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-fg-secondary">
              {contacts.length === 0
                ? 'No contacts synced to this campaign yet.'
                : 'No sequence steps found. Build your sequence first.'}
            </p>
          </div>
        </div>
        <div className="border-t border-line bg-surface px-6 py-4">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 rounded-md border border-line px-4 py-2 text-sm hover:bg-surface-hover"
          >
            <ArrowLeft className="size-4" />
            Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Review Emails"
        badge={
          <span className="rounded-full bg-surface-alt px-2 py-0.5 text-xs text-fg-secondary">
            Optional
          </span>
        }
        description={
          readOnly
            ? 'Preview the emails each contact will receive.'
            : 'Preview the exact email each contact will receive. Edit any individual email to personalize further.'
        }
        rightSlot={
          totalOverrides > 0 ? (
            <div className="shrink-0 text-right">
              <p className="text-2xl font-semibold text-brand">
                {totalOverrides}
              </p>
              <p className="text-xs text-fg-secondary">
                custom edit{totalOverrides !== 1 ? 's' : ''}
              </p>
            </div>
          ) : undefined
        }
      />

      {/* Active campaign banner */}
      {readOnly && (
        <div className="border-b border-warning-border bg-warning-bg-subtle px-6 py-3">
          <p className="mx-auto max-w-[1200px] text-sm text-warning">
            This campaign is active. Pause the campaign to edit email overrides.
          </p>
        </div>
      )}

      {/* Split panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Contact list */}
        <div className="flex w-[320px] shrink-0 flex-col border-r border-line bg-surface">
          <div className="border-b border-line p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-fg-secondary" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg bg-surface-hover py-2 pl-9 pr-4 text-sm outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>
            <p className="mt-2 px-1 text-xxs text-fg-secondary">
              {filteredContacts.length} contact
              {filteredContacts.length !== 1 ? 's' : ''} in sequence
            </p>
          </div>
          <div className="flex-1 overflow-auto">
            {filteredContacts.map((contact) => {
              const isSelected = resolvedContactId === contact.contact_id
              const leadId =
                leadIdForContact[contact.contact_id] ?? contact.contact_id
              const hasCustom = contactsWithOverrides.has(leadId)

              return (
                <button
                  type="button"
                  key={contact.contact_id}
                  onClick={() => {
                    setSelectedContactId(contact.contact_id)
                    setEditingKey(null)
                  }}
                  className={cn(
                    'w-full border-b border-line px-4 py-3 text-left transition-colors',
                    isSelected ? 'bg-brand/5' : 'hover:bg-surface-hover'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'flex size-9 shrink-0 items-center justify-center rounded-full',
                        isSelected ? 'bg-brand/10' : 'bg-surface-hover'
                      )}
                    >
                      <User
                        className={cn(
                          'size-4',
                          isSelected ? 'text-brand' : 'text-fg-secondary'
                        )}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'truncate text-sm',
                            isSelected && 'text-brand'
                          )}
                        >
                          {contact.full_name}
                        </span>
                        {hasCustom && (
                          <span className="shrink-0 rounded-full border border-warning-border bg-warning-bg-subtle px-1.5 py-0.5 text-xxs text-warning">
                            Edited
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-fg-secondary">
                        <Building2 className="size-3 shrink-0" />
                        <span className="truncate">{contact.company_name}</span>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right: Email preview/editor */}
        <div className="flex-1 overflow-auto bg-surface-hover/30">
          {selectedContact && currentStep && (
            <div className="mx-auto max-w-3xl p-6">
              {/* Contact header */}
              <div className="mb-4 rounded-xl border border-line bg-surface p-4">
                <div className="flex items-center gap-3">
                  <div className="flex size-11 items-center justify-center rounded-full bg-brand/10">
                    <User className="size-5 text-brand" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-fg">
                      {selectedContact.full_name}
                    </p>
                    <p className="text-xs text-fg-secondary">
                      {selectedContact.title}
                      {selectedContact.title && selectedContact.company_name
                        ? ' at '
                        : ''}
                      {selectedContact.company_name}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-fg-secondary">
                      <Mail className="size-3" />
                      {selectedContact.email}
                    </p>
                  </div>
                  {overrideKey &&
                    contactsWithOverrides.has(
                      parseInt(overrideKey.split('_')[0], 10)
                    ) && (
                      <span className="rounded-full border border-warning-border bg-warning-bg-subtle px-2.5 py-1 text-xs text-warning">
                        Edited
                      </span>
                    )}
                </div>
              </div>

              {/* Step tabs */}
              {steps.length > 1 && (
                <div className="mb-4 flex gap-1.5">
                  {steps.map((step, index) => {
                    const stepKey = resolvedLeadId
                      ? `${resolvedLeadId}_${step.id}`
                      : null
                    const stepHasOverride = stepKey
                      ? overrideMap[stepKey] !== undefined
                      : false

                    return (
                      <button
                        type="button"
                        key={step.id}
                        onClick={() => {
                          setActiveStepId(step.id)
                          setEditingKey(null)
                        }}
                        className={cn(
                          'flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs transition-colors',
                          resolvedStepId === step.id
                            ? 'bg-brand text-white'
                            : 'border border-line bg-surface text-fg-secondary hover:bg-surface-hover'
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-5 items-center justify-center rounded-full text-xxs',
                            resolvedStepId === step.id
                              ? 'bg-white/20'
                              : 'bg-surface-hover'
                          )}
                        >
                          {index + 1}
                        </span>
                        Step {index + 1}
                        {stepHasOverride && <Edit3 className="size-2.5" />}
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Email preview / editor card */}
              <div className="overflow-hidden rounded-xl border border-line bg-surface">
                {/* Toolbar */}
                <div className="flex items-center justify-between border-b border-line bg-surface-hover/50 px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-fg-secondary">
                    <EyeIcon className="size-3.5" />
                    {isEditing ? 'Editing' : 'Preview'} for Step{' '}
                    {steps.indexOf(currentStep) + 1}
                    {hasOverride && !isEditing && (
                      <span className="rounded-full bg-brand/10 px-2 py-0.5 text-brand">
                        Edited
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={resetOverride}
                          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-fg-secondary hover:bg-surface-hover"
                        >
                          <RotateCcw className="size-3" />
                          Reset to default
                        </button>
                        <button
                          type="button"
                          onClick={saveEdit}
                          disabled={!canSaveCurrentOverride}
                          className="flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs text-white hover:bg-brand/90 disabled:opacity-50"
                        >
                          <CheckCircle2 className="size-3.5" />
                          {savingKeys.has(overrideKey!)
                            ? 'Saving...'
                            : 'Done Editing'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowTestEmailModal(true)}
                          disabled={!currentStep?.id}
                          title={
                            !currentStep?.id
                              ? 'Save the sequence first to send a test'
                              : `Send a test of Step ${steps.indexOf(currentStep) + 1}`
                          }
                          className="flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Send className="size-3.5" />
                          Send test email
                        </button>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={startEditing}
                            className="flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs hover:bg-surface-hover"
                          >
                            <Edit3 className="size-3.5" />
                            Edit for{' '}
                            {(selectedContact.full_name || '').split(' ')[0]}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Email content */}
                <div className="space-y-4 p-5">
                  {/* Subject */}
                  <div>
                    <label className="mb-1.5 block text-xxs uppercase tracking-wider text-fg-secondary">
                      Subject
                    </label>
                    {isEditing ? (
                      <div>
                        <input
                          type="text"
                          value={overrideMap[overrideKey!]?.subject ?? ''}
                          onChange={(e) =>
                            updateOverride('subject', e.target.value)
                          }
                          className="w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/20"
                        />
                        <VariableInsertBar
                          onInsert={(variable) => {
                            const current =
                              overrideMap[overrideKey!]?.subject ?? ''
                            if (variable.hasMissingValues) {
                              toast.warning(getMissingValuesMessage(variable))
                            }
                            updateOverride('subject', current + variable.value)
                          }}
                          variables={allVariables}
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg bg-surface-hover/50 px-3 py-2 text-sm">
                        {selectedRendered?.subject || (
                          <span className="italic text-fg-secondary">
                            No subject
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {hasSelectedEmailWarnings && (
                    <div className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-bg-subtle px-3 py-2 text-xs text-warning">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                      <div>
                        <p>
                          Some variables could not be filled for this contact.
                          Fix or remove them before continuing.
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {selectedEmailWarnings.map((warning) => (
                            <code
                              key={warning}
                              className="rounded bg-warning-bg px-1.5 py-0.5 text-xxs"
                            >
                              {warning}
                            </code>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Body */}
                  <div>
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <label className="block text-xxs uppercase tracking-wider text-fg-secondary">
                        Body
                      </label>
                      <FormatBadge plainText={isPlainText} />
                    </div>
                    {isEditing ? (
                      <OverrideBodyEditor
                        value={overrideMap[overrideKey!]?.body ?? ''}
                        onChange={(val) => updateOverride('body', val)}
                        variables={allVariables}
                      />
                    ) : (
                      <div className="min-h-[200px] rounded-lg bg-surface-hover/50 px-4 py-3 text-sm leading-relaxed">
                        {isPlainText ? (
                          <pre className="whitespace-pre-wrap break-words font-sans text-sm text-fg [overflow-wrap:anywhere]">
                            {htmlToBisonPlainText(selectedRendered?.body || '')}
                          </pre>
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                selectedRendered?.body || ''
                              ),
                            }}
                          />
                        )}
                        <EmailFooterPreview signature={senderSignature} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {unresolvedEmailWarnings.length > 0 && (
        <div className="border-t border-warning-border bg-warning-bg-subtle px-8 py-2 text-xs text-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <div>
              <p>
                {unresolvedEmailWarnings.length} email
                {unresolvedEmailWarnings.length === 1 ? '' : 's'} still have
                variables that could not be filled. Review those emails before
                launch.
              </p>
              <p className="mt-1">
                First issue: {unresolvedEmailWarnings[0].contact.full_name},
                step {steps.indexOf(unresolvedEmailWarnings[0].step) + 1}:{' '}
                {unresolvedEmailWarnings[0].warnings.join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      <SendStepTestEmailModal
        show={showTestEmailModal}
        onHide={() => setShowTestEmailModal(false)}
        bisonStepId={currentStep?.id ?? null}
        bisonCampaignId={bisonCampaignId}
        stepNumber={currentStep ? steps.indexOf(currentStep) + 1 : 0}
        unresolvedVariables={selectedEmailWarnings}
        renderedFor={renderedForSelectedContact}
      />

      <WizardStepFooter>
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <ArrowLeft className="size-4" />
          Back
        </FbButton>
        <FbButton
          type="button"
          onClick={onNext}
          disabled={unresolvedEmailWarnings.length > 0}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          {primaryButtonLabel}
          <ArrowRight className="size-4" />
        </FbButton>
      </WizardStepFooter>
    </div>
  )
}

/** Rich text editor for override body — uses the same Quill editor as the sequence builder. */
function OverrideBodyEditor({
  value,
  onChange,
  variables,
}: {
  value: string
  onChange: (value: string) => void
  variables: ReadonlyArray<EmailVariable>
}) {
  const pendingFocusPosition = useRef<number | null>(null)

  const { uniqueId, quillEditor, cursorPositionRef } = useEditorCore({
    value,
    onChange: (val) => onChange(val ?? ''),
    placeholder: 'Write your custom email content...',
  })

  const insertVariable = (variable: EmailVariable) => {
    if (!quillEditor) return
    if (variable.hasMissingValues) {
      toast.warning(getMissingValuesMessage(variable))
    }
    const maxPosition = Math.max(0, quillEditor.getLength() - 1)
    const position = Math.min(cursorPositionRef.current, maxPosition)
    quillEditor.insertText(position, variable.value)
    const newPosition = position + variable.value.length
    cursorPositionRef.current = newPosition
    pendingFocusPosition.current = newPosition
  }

  const handleDropdownOpenChange = (open: boolean) => {
    if (!open && pendingFocusPosition.current !== null && quillEditor) {
      const pos = pendingFocusPosition.current
      pendingFocusPosition.current = null
      requestAnimationFrame(() => {
        quillEditor.focus()
        quillEditor.setSelection(pos, 0)
      })
    }
  }

  const variableDropdownButton = (
    <DropdownMenu onOpenChange={handleDropdownOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Insert Variable"
          className="inline-flex items-center gap-1 rounded bg-white px-2 text-xs font-medium"
        >
          <span>Variables</span>
          <ChevronDown className="size-3 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {variables.map((variable) => (
          <DropdownMenuItem
            key={variable.value}
            onClick={() => insertVariable(variable)}
          >
            <span className="flex items-center gap-1.5">
              {variable.label}
              {variable.isCampaignColumn && (
                <span className="rounded bg-surface-hover px-1 text-xxs uppercase text-fg-secondary">
                  {variable.isManual ? 'Manual' : 'AI'}
                </span>
              )}
              {variable.hasMissingValues && (
                <AlertTriangle className="size-3 shrink-0 text-warning" />
              )}
            </span>
            {variable.hasMissingValues && (
              <span className="ml-2 text-xxs text-warning">
                {getMissingValuesLabel(variable)}
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div className="rounded-lg border border-line bg-surface">
      <div id={`editor-${uniqueId}`} className="min-h-[200px] bg-surface" />
      <EditorToolbar
        uniqueId={uniqueId}
        additionalToolbarButtons={variableDropdownButton}
      />
    </div>
  )
}

/** Small bar showing clickable variable tokens for insertion (used for subject field). */
function VariableInsertBar({
  onInsert,
  variables,
}: {
  onInsert: (variable: EmailVariable) => void
  variables: ReadonlyArray<EmailVariable>
}) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <span className="text-xxs text-fg-secondary">Insert:</span>
      {variables.map((v) => (
        <button
          type="button"
          key={v.token}
          onClick={() => onInsert(v)}
          title={
            v.hasMissingValues
              ? getMissingValuesMessage(v)
              : `Insert ${v.label}`
          }
          className={cn(
            'inline-flex items-center gap-1 rounded border border-line bg-surface-hover px-1.5 py-0.5 text-xxs text-fg-secondary hover:bg-brand/10 hover:text-brand',
            v.hasMissingValues && 'border-warning-border text-warning'
          )}
        >
          {v.label}
          {v.hasMissingValues && (
            <AlertTriangle className="size-3 shrink-0 text-warning" />
          )}
        </button>
      ))}
    </div>
  )
}
